import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';

import { OperationService } from '../../../services';
import type { OperationResult, SourceImageRef } from '../../../domain';
import {
  DEFAULT_OUTPAINT_STATE,
  buildOutpaintRequest,
  hasSourceImage,
  type OutpaintFormState,
} from './buildRequests';
import { useOperationMutation } from './useOperationMutation';
import { submitErrorMessage } from '../../../components/auth/signInRequiredError';
import { mergePromptWithSwatches } from '../../shared/panels/swatchSelection';

interface Props {
  ensureSession: () => Promise<string>;
  isCreatingSession?: boolean;
  source: SourceImageRef | undefined;
  swatchPrompt?: string;
  onResult?: (result: OperationResult) => void;
}

export const OutpaintPanel: React.FC<Props> = ({
  ensureSession,
  isCreatingSession = false,
  source,
  swatchPrompt,
  onResult,
}) => {
  const [state, setState] = useState<OutpaintFormState>(DEFAULT_OUTPAINT_STATE);
  const [validationError, setValidationError] = useState<string | null>(null);

  const mutation = useOperationMutation(OperationService.outpaint);

  const hasSource = hasSourceImage(source);

  const handleSubmit = async () => {
    if (!source) {
      setValidationError('A source image is required to outpaint.');
      return;
    }
    try {
      const sid = await ensureSession();
      const mergedPrompt = mergePromptWithSwatches(state.prompt, swatchPrompt ?? '');
      const request = buildOutpaintRequest({ ...state, prompt: mergedPrompt }, sid, source);
      setValidationError(null);
      const result = await mutation.mutateAsync(request);
      onResult?.(result);
    } catch (err) {
      setValidationError(submitErrorMessage(err));
    }
  };

  const submitDisabled = mutation.isPending || isCreatingSession || !hasSource;

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} alignItems="center">
        <OpenInFullIcon fontSize="small" />
        <Typography variant="subtitle2">Expand canvas</Typography>
      </Stack>

      {!hasSource && (
        <Alert severity="info">Add or select a source image to expand.</Alert>
      )}

      <TextField
        label="Prompt (optional)"
        value={state.prompt}
        onChange={e => setState(s => ({ ...s, prompt: e.target.value }))}
        placeholder="Describe what should appear in the new space"
        multiline
        minRows={1}
        maxRows={3}
        size="small"
        fullWidth
      />

      <Stack direction="row" spacing={1}>
        <TextField
          label="Top (px)"
          type="number"
          value={state.top}
          onChange={e => setState(s => ({ ...s, top: Number(e.target.value) }))}
          size="small"
          inputProps={{ min: 0, max: 2048, step: 32 }}
        />
        <TextField
          label="Right (px)"
          type="number"
          value={state.right}
          onChange={e => setState(s => ({ ...s, right: Number(e.target.value) }))}
          size="small"
          inputProps={{ min: 0, max: 2048, step: 32 }}
        />
        <TextField
          label="Bottom (px)"
          type="number"
          value={state.bottom}
          onChange={e => setState(s => ({ ...s, bottom: Number(e.target.value) }))}
          size="small"
          inputProps={{ min: 0, max: 2048, step: 32 }}
        />
        <TextField
          label="Left (px)"
          type="number"
          value={state.left}
          onChange={e => setState(s => ({ ...s, left: Number(e.target.value) }))}
          size="small"
          inputProps={{ min: 0, max: 2048, step: 32 }}
        />
      </Stack>

      {validationError && <Alert severity="warning">{validationError}</Alert>}
      {mutation.isError && (
        <Alert severity="error">
          {mutation.error instanceof Error ? mutation.error.message : 'Outpaint failed.'}
        </Alert>
      )}

      <Box>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitDisabled}
          startIcon={
            mutation.isPending || isCreatingSession ? (
              <CircularProgress size={16} />
            ) : (
              <OpenInFullIcon />
            )
          }
        >
          {isCreatingSession ? 'Starting session…' : mutation.isPending ? 'Expanding…' : 'Expand canvas'}
        </Button>
      </Box>
    </Stack>
  );
};
